import { listActiveComponentEntries } from "./listActiveComponentEntries";
import type { ComponentStoreData } from "./types";
import { contentOptimizationComponentNames } from "./types";

/**
 * Applies custom user overrides to the component store data
 * @param componentStoreData - The component store data to apply the overrides to
 * @param selectedComponentOverrides - The selected component overrides
 * @returns The component store data with the custom user overrides applied
 */
export const applyCustomUserOverrides = (
  componentStoreData: ComponentStoreData | undefined,
  selectedComponentOverrides: Record<string, string>
): ComponentStoreData => {
  if (!componentStoreData) {
    return { components: {} };
  }
  const baseComponents = componentStoreData.components;
  const deactivatedValues = componentStoreData.deactivatedValues ?? {};
  const baseMetadata = componentStoreData.variantMetadata;
  const nextComponents = Object.fromEntries(
    contentOptimizationComponentNames.map((name) => [name, [] as string[]])
  ) as ComponentStoreData["components"];
  const nextMetadata: ComponentStoreData["variantMetadata"] = {};

  contentOptimizationComponentNames.forEach((name) => {
    const deactivatedForComponent = new Set(deactivatedValues[name] ?? []);
    const selectableEntries = listActiveComponentEntries(baseComponents?.[name] ?? [], deactivatedForComponent);

    const override = selectedComponentOverrides?.[name];
    const chosenEntry =
      (override && selectableEntries.find((entry) => entry.value === override)) || selectableEntries[0];
    const chosen = chosenEntry?.value ?? "";
    nextComponents[name] = chosen !== "" ? [chosen] : [];

    if (baseMetadata?.[name] && chosenEntry) {
      nextMetadata[name] = [baseMetadata[name][chosenEntry.index] ?? {}];
    }
  });

  if (Object.keys(nextMetadata).length > 0) {
    return { components: nextComponents, variantMetadata: nextMetadata };
  }

  return { components: nextComponents };
};
